import React, { useEffect, useState } from "react";
import { ChevronDownIcon, DownloadIcon, Eye, EyeOff, X, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import setImg from "@/assets/home/set.png";
import { useTheme } from "@/contexts/ThemeContext.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import useCounterStore from "@/store";
import { useGlobalStore } from "@/store/store";
import { string } from "zod";
import { useTranslation } from "@/locales";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { HoverTip } from "./hoverTip";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Edit2, Check } from "lucide-react";
import { log } from "console";
import AuthService from "@/services/auth";
import { modelService, ModalInfo } from "@/services/modelService";

// import { IconButton } from "@/components/ui/icon-button";

interface SettingsDialogProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultTab?: "general" | "password";
  mode?: 'dialog' | 'page';
}

type ThemeMode = "light" | "dark" | "system";

export default function SettingsDialog({ isOpen, onOpenChange, defaultTab = "general", mode: initialMode }: SettingsDialogProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const mode = initialMode || (isMobile ? 'page' : 'dialog');
  const [activeTab, setActiveTab] = useState<"general" | "password">(defaultTab);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmNewPwd, setConfirmNewPwd] = useState("");
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const { themeMode, toggleTheme } = useTheme();
  const { modal, setModal } = useCounterStore();
  const { user } = useGlobalStore();
  const { t, updateNickName } = useTranslation();
  const themeKey = themeMode as ThemeMode;
  const [nickName, setNickName] = useState(user?.nickname || "");
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [tempNickName, setTempNickName] = useState("");
  const token = AuthService.getAccessToken();
  const teamId = AuthService.setTeamId();
  const Theme: Record<ThemeMode, string> = {
    system: t("theme.system"),
    light: t("theme.light"),
    dark: t("theme.dark"),
  };
  const [modalList, setModalList] = useState<ModalInfo[]>([]);
  const handleTabChange = (value: "general" | "password") => {
    setActiveTab(value);
  };

  useEffect(() => {
    // 只在组件首次挂载时获取模型列表
    getModalList();
    console.log("teamId", teamId);
  }, []);

  useEffect(() => {
    console.log(AuthService.setTeamId());
    console.log("teamId", teamId);
  }, [isOpen]);

  // 当用户信息变化时更新昵称
  useEffect(() => {
    if (user?.nickname) {
      setNickName(user.nickname);
    }
  }, [user?.nickname]);

  const getModalList = async () => {
    // 判断用户是否登录
    if (!token) {
      return;
    }
    try {
      // 使用模型服务获取模型列表（带缓存）
      const res = await modelService.getModelList();
      setModalList(res);
    } catch (error) {
      console.error("获取模型列表失败:", error);
    }
  };
  const validatePassword = () => {
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[-!@#$%^&*_+=?]).{8,}$/;
    if (!regex.test(newPwd)) {
      alert(t("settings.passwordValidationError"));
      return false;
    }
    if (newPwd !== confirmNewPwd) {
      alert(t("settings.passwordMismatchError"));
      return false;
    }
    return true;
  };

  const modelName = (value: string) => {
    const info = modalList.find((item) => item.code === value);
    return info?.name || value;
  };
  const handleSavePassword = () => {
    if (validatePassword()) {
      console.log("当前密码:", currentPwd);
      console.log("新密码:", newPwd);
      alert(t("settings.passwordChangeSuccess"));
      setCurrentPwd("");
      setNewPwd("");
      setConfirmNewPwd("");
    }
  };

  // 移动端设置页面
  const renderMobilePage = () => (
    <div className="h-full bg-gray-50 dark:bg-zinc-900 flex flex-col flex-1">
      <header className="flex items-center p-4 border-b bg-white dark:bg-zinc-800 sticky top-0 z-20 h-16">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="-ml-2">
          <ChevronLeft className="h-6 w-6" />
        </Button>
        <h2 className="text-lg font-semibold text-center flex-1">{t("settings.title")}</h2>
        <div className="w-8"></div>
      </header>
      <div className="flex-1 overflow-y-auto">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 sticky top-0 bg-gray-50 dark:bg-zinc-900 z-10">
            <TabsTrigger value="general">{t("settings.general")}</TabsTrigger>
            <TabsTrigger value="password">{t("settings.password")}</TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="px-4 py-6 space-y-6">
            <div className="space-y-2">
              <Label>{t("settings.theme")}</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    {Theme[themeKey]} <ChevronDownIcon className={"w-4 h-4"} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                  {Object.keys(Theme).map((key) => (
                    <DropdownMenuItem key={key} onClick={() => toggleTheme(key as ThemeMode)}>
                      {Theme[key as ThemeMode]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <span>{t("settings.language")}</span>
                <HoverTip content={t("settings.languagePreferenceDescription")}>
                  <Info className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer self-center" />
                </HoverTip>
              </Label>
              <LanguageSwitcher variant="compact" showIcon={false} className="w-full" />
            </div>
            <div className="space-y-2">
              <Label>{t("settings.nickname")}</Label>
              <div className="flex items-center gap-2">
                {isEditingNickname ? (
                  <>
                    <Input
                      id="nickname-mobile"
                      type="text"
                      maxLength={10}
                      placeholder={t("settings.nickname")}
                      className="flex-1"
                      value={tempNickName}
                      onChange={(e) => setTempNickName(e.target.value)}
                      autoFocus
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={async () => {
                        if (tempNickName.trim() && tempNickName.trim() !== user?.nickname) {
                          try {
                            await updateNickName(tempNickName.trim());
                            setNickName(tempNickName.trim());
                            toast.success(t("settings.nicknameUpdateSuccess"));
                          } catch (error) {
                            toast.error(t("settings.nicknameUpdateError"));
                            setTempNickName(nickName);
                          }
                        }
                        setIsEditingNickname(false);
                      }}>
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        setTempNickName(nickName);
                        setIsEditingNickname(false);
                      }}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <span className="flex-1 truncate text-muted-foreground">{nickName || t("settings.nicknamePlaceholder")}</span>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="-mr-2 h-8 w-8"
                      onClick={() => {
                        setTempNickName(nickName);
                        setIsEditingNickname(true);
                      }}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            {modalList.length > 1 && teamId == 1 && (
              <div className="space-y-2">
                <Label>{t("settings.model")}</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      {modelName(modal)} <ChevronDownIcon className={"w-4 h-4"} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                    {modalList.map((key) => (
                      <DropdownMenuItem key={key.code} onClick={() => setModal(key.code)}>
                        {key.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </TabsContent>
          <TabsContent value="password" className="p-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPwd-mobile">{t("settings.currentPassword")}</Label>
                <div className="relative">
                  <Input
                    id="currentPwd-mobile"
                    type={showCurrentPwd ? "text" : "password"}
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    placeholder={t("settings.currentPasswordPlaceholder")}
                  />
                  <Button
                    className="absolute top-1/2 right-2 -translate-y-1/2 h-7 w-7"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowCurrentPwd(!showCurrentPwd)}>
                    {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPwd-mobile">{t("settings.newPassword")}</Label>
                <div className="relative">
                  <Input
                    id="newPwd-mobile"
                    type={showNewPwd ? "text" : "password"}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder={t("settings.newPasswordPlaceholder")}
                  />
                  <Button
                    className="absolute top-1/2 right-2 -translate-y-1/2 h-7 w-7"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowNewPwd(!showNewPwd)}>
                    {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t("settings.passwordRequirement")}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmNewPwd-mobile">{t("settings.confirmPassword")}</Label>
                <div className="relative">
                  <Input
                    id="confirmNewPwd-mobile"
                    type={showConfirmPwd ? "text" : "password"}
                    value={confirmNewPwd}
                    onChange={(e) => setConfirmNewPwd(e.target.value)}
                    placeholder={t("settings.confirmPasswordPlaceholder")}
                  />
                  <Button
                    className="absolute top-1/2 right-2 -translate-y-1/2 h-7 w-7"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowConfirmPwd(!showConfirmPwd)}>
                    {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <Button variant="default" onClick={handleSavePassword} className="w-full mt-4 !mt-6">
                {t("settings.savePassword")}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );

  if (mode === 'page') {
    return renderMobilePage();
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md" style={{ padding: 0, minWidth: "600px" }}>
        <DialogHeader style={{ padding: "16px 24px" }}>
          <DialogTitle>{t("settings.title")}</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => handleTabChange(value as "general" | "password")}>
          <div className="flex" style={{ height: "420px" }}>
            {/* 左侧标签列表 */}
            <div
              style={{
                //backgroundColor: ' var(--background)'
                padding: "10px",
                borderRadius: "0 0 0 6px",
                width: "150px",
              }}
              className=" dark:bg-[#09090b] bg-[#f8f8f8]">
              <HoverTip content={t("settings.general")}>
                <div
                  onClick={() => handleTabChange("general")}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                  }}>
                  <img src={setImg} style={{ height: "100%" }} className="mr-2" alt="" />
                  <span className="truncate">{t("settings.general")}</span>
                </div>
              </HoverTip>

              <HoverTip content={t("settings.password")}>
                <div
                  onClick={() => handleTabChange("password")}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 15px",
                  }}>
                  <img src={setImg} style={{ height: "100%" }} className="mr-2" alt="" />
                  <span className="truncate">{t("settings.password")}</span>
                </div>
              </HoverTip>
            </div>

            <div className={"flex-1"} style={{ padding: " 0px 15px" }}>
              {/* TabsContent 现在是 Tabs 的直接子元素 */}
              <TabsContent value="general" className="mt-0">
                <h3 className="text-lg font-medium">{t("settings.general")}</h3>
                <div className={"flex-1"}>
                  <div className="flex justify-between mt-5 text-[14px]">
                    <div>{t("settings.theme")}</div>
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <div className={"flex items-center cursor-pointer"}>
                          {Theme[themeKey]} <ChevronDownIcon className={"w-4 h-4"} />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {Object.keys(Theme).map((key) => (
                          <DropdownMenuItem key={key} onClick={() => toggleTheme(key as ThemeMode)}>
                            {Theme[key as ThemeMode]}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex justify-between mt-5 text-[14px]">
                    <div className="flex items-center gap-1">
                      <span>{t("settings.language")}</span>
                      <HoverTip content={t("settings.languagePreferenceDescription")}>
                        <Info className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer self-center" />
                      </HoverTip>
                    </div>
                    <LanguageSwitcher variant="compact" showIcon={false} className="" />
                  </div>
                  <div className="flex justify-between mt-5 text-[14px]">
                    <div>{t("settings.nickname")}</div>
                    <div className="w-[200px] flex items-center gap-2">
                      {isEditingNickname ? (
                        <>
                          <Input
                            id="nickname"
                            type="text"
                            maxLength={10}
                            placeholder={t("settings.nickname")}
                            className="flex-1"
                            value={tempNickName}
                            onChange={(e) => setTempNickName(e.target.value)}
                            autoFocus
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="!p-1 !h-4 !w-4"
                            onClick={async () => {
                              if (tempNickName.trim() && tempNickName.trim() !== user?.nickname) {
                                try {
                                  await updateNickName(tempNickName.trim());
                                  setNickName(tempNickName.trim());
                                  toast.success(t("settings.nicknameUpdateSuccess"));
                                } catch (error) {
                                  toast.error(t("settings.nicknameUpdateError"));
                                  setTempNickName(nickName);
                                }
                              }
                              setIsEditingNickname(false);
                            }}>
                            <Check className="h-2 w-2" style={{ height: "0.8rem", width: "0.8rem" }} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="!p-1 !h-4 !w-4"
                            onClick={() => {
                              setTempNickName(nickName);
                              setIsEditingNickname(false);
                            }}>
                            <X style={{ height: "0.8rem", width: "0.8rem" }} />
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 text-right truncate">
                            {nickName || t("settings.nicknamePlaceholder")}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="!p-1 !h-4 !w-4"
                            onClick={() => {
                              setTempNickName(nickName);
                              setIsEditingNickname(true);
                            }}>
                            <Edit2 style={{ height: "0.8rem", width: "0.8rem" }} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {modalList.length > 1 && teamId == 1 && (
                    <div className="flex justify-between mt-5 text-[14px]">
                      <div>{t("settings.model")}</div>
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <div className={"flex items-center cursor-pointer"}>
                            {modelName(modal)} <ChevronDownIcon className={"w-4 h-4"} />
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {modalList.map((key) => (
                            <DropdownMenuItem key={key.code} onClick={() => setModal(key.code)}>
                              {key.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="password" className="mt-0">
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPwd">{t("settings.currentPassword")}</Label>
                    <div className="flex space-x-2">
                      <Input
                        id="currentPwd"
                        type={showCurrentPwd ? "text" : "password"}
                        value={currentPwd}
                        onChange={(e) => setCurrentPwd(e.target.value)}
                        placeholder={t("settings.currentPasswordPlaceholder")}
                      />
                      <Button
                        className="p-0"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowCurrentPwd(!showCurrentPwd)}>
                        {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPwd">{t("settings.newPassword")}</Label>
                    <div className="flex space-x-2">
                      <Input
                        id="newPwd"
                        type={showNewPwd ? "text" : "password"}
                        value={newPwd}
                        onChange={(e) => setNewPwd(e.target.value)}
                        placeholder={t("settings.newPasswordPlaceholder")}
                      />
                      <Button className="p-0" variant="ghost" size="icon" onClick={() => setShowNewPwd(!showNewPwd)}>
                        {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("settings.passwordRequirement")}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmNewPwd">{t("settings.confirmPassword")}</Label>
                    <div className="flex space-x-2">
                      <Input
                        id="confirmNewPwd"
                        type={showConfirmPwd ? "text" : "password"}
                        value={confirmNewPwd}
                        onChange={(e) => setConfirmNewPwd(e.target.value)}
                        placeholder={t("settings.confirmPasswordPlaceholder")}
                      />
                      <Button
                        className="p-0"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowConfirmPwd(!showConfirmPwd)}>
                        {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <Button variant="default" onClick={handleSavePassword} className="w-full mt-2">
                    {t("settings.savePassword")}
                  </Button>
                </div>
              </TabsContent>
            </div>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
